import { ToastProvider } from '@citizen-walk/shared-ui';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App';
import { notifyNeedRefresh, setUpdateSW } from './features/shell';
import { registerPmtilesProtocol } from './features/map';
import { initI18n } from './i18n';
import { initGa4 } from './lib/ga4';
import { initSentry } from './lib/sentry';
import './index.css';

// Sprint BG-DataSettings 19 (U-P12): i18n を起動時に初期化。
// localStorage `cw.locale` を読込 → @citizen-walk/shared-i18n の current locale に反映。
// SW / Sentry / GA4 より前に呼ぶ理由は無いが、React tree mount より前に必ず終わっている
// 必要があるため top で同期実行 (旧 v1 main.tsx の setupI18n() 踏襲)。
initI18n();

// Sprint BG Part 2 — Sentry / GA4 (configuration foundation only)。
// DSN / Measurement ID が .env.local で未設定なら silent no-op。
// dep (@sentry/react) は未 install のため、Sentry は実 init されない (loadSentry が null 返却)。
void initSentry();
initGa4();

// PMTiles `pmtiles://` protocol を maplibre-gl に登録 (Phase 5.3 R-33 step 6、v1 main.tsx 踏襲)。
// App 内の MapView が `pmtiles://blob:...` source を使う前に必須 (global、idempotent)。
registerPmtilesProtocol();

// 2026-10-09 (docs/28 レビュー): 写真のキャッシュをサムネ / 原寸に分け、CORS で成功だけを保存する
// 新しいキャッシュ (storage-thumbs-v2 / storage-photos-v2、vite.config.ts) に移した。旧キャッシュ
// (opaque 応答、失敗も保存しうる) は使わないので消す。新しい JS は新しい Service Worker が有効に
// なってから読まれるため、旧 Service Worker が作り直すことはない。
if (typeof caches !== 'undefined') {
  void caches.delete('storage-cache').catch(() => undefined);
}

// user 第 15 弾 (U-P10): navigator.storage.persist() で OS の自動削除対象から除外を要請。
// Firefox / Chromium で persistent storage を request、ユーザー許可後は quota 内容が
// (Service Worker / IndexedDB / Cache Storage) は automatic eviction 対象外になる。
// 失敗しても致命的でないため握りつぶし (旧 v1 main.tsx 踏襲)。
if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
  void navigator.storage.persist().then(
    (granted) => {
      if (granted) {
        // eslint-disable-next-line no-console
        console.info('[Storage] persistent storage granted');
      }
    },
    (err) => {
      // eslint-disable-next-line no-console
      console.warn('[Storage] persistent storage request failed', err);
    },
  );
}

// Service Worker 登録 + 任意更新 (prompt 方式、2026-06-12)。
//  - `immediate: true` → render 前に SW 登録開始 (mount block しない)
//  - `onNeedRefresh` → 新 SW 検出時に**即リロードせず** notifyNeedRefresh() で UI に通知し、
//    `<UpdateBanner>` の「更新」ボタンでユーザーが任意のタイミングで適用する (操作中の強制
//    リロードを廃止)。実際の適用は applyUpdate() → updateSW(true) (skipWaiting + reload)。
//    「久しぶりに開いた」人は待機 SW がフレッシュ起動時に自動有効化されるため取り残されない。
//  - `onOfflineReady` → console.log のみ (UI 通知は別途)。
// vite-plugin-pwa が dev では noop を返すため、`devOptions.enabled: false` 設定下でも安全。
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    notifyNeedRefresh();
  },
  onOfflineReady() {
    // eslint-disable-next-line no-console
    console.info('[PWA] offline ready (precache 完了、オフラインで起動可能)');
  },
});
// 「更新」ボタン (UpdateBanner) から呼べるよう updateSW を共有ストアに登録。
setUpdateSW(updateSW);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find the root element');
}

createRoot(rootElement).render(
  <StrictMode>
    <ToastProvider>
      <App />
    </ToastProvider>
  </StrictMode>,
);

// env-2 (docs/111 Wave 1): ここに到達した = firebase.ts の TLA を抜けてブートが進んだ、ということ。
// index.html の静的フォールバックが 8s 後に出す「再読み込み」案内を止める (#boot-fallback は React の
// 初回 render が #root の子として置換するため別途除去は不要)。render が throw した場合はタイマーを
// 残し、フォールバックの再読み込み導線を表示させる (この行に到達しないため)。
(window as unknown as { __cwClearBootTimer?: () => void }).__cwClearBootTimer?.();
